/**
 * Positioning helper for inventory-driven panel compositions.
 *
 * Every panel inventory expresses control positions in canonical
 * viewBox units (1:10 mm scale per the rendering discipline). The
 * panel chassis renders at its physical aspect ratio; each control
 * positions absolutely within the chassis as a percentage of the
 * viewBox.
 *
 * This helper converts an inventory position+dimensions pair into a
 * single CSS string that places the control. No px, no clamping, no
 * runtime calculation — the percentages are computed once at render
 * time and the browser handles the rest.
 */

export interface Position2d {
  readonly x: number;
  readonly y: number;
}

export interface Dimensions2d {
  readonly w: number;
  readonly h: number;
}

export interface ViewBox2d {
  readonly width: number;
  readonly height: number;
}

const FRACTION_DIGITS = 4;

function pct(num: number, denom: number): string {
  return ((num / denom) * 100).toFixed(FRACTION_DIGITS);
}

/**
 * Compose the CSS declarations that place a positioned control inside
 * a chassis whose own dimensions match the supplied viewBox.
 *
 * The returned string is intended for the `style` attribute. The
 * caller is responsible for ensuring the parent establishes a
 * positioning context (chassis-frame does this).
 */
export function positionStyle(
  position: Position2d,
  dimensions: Dimensions2d,
  viewBox: ViewBox2d,
): string {
  const left = pct(position.x, viewBox.width);
  const top = pct(position.y, viewBox.height);
  const width = pct(dimensions.w, viewBox.width);
  const height = pct(dimensions.h, viewBox.height);
  return `position:absolute;left:${left}%;top:${top}%;width:${width}%;height:${height}%`;
}

/**
 * Compose the CSS aspect-ratio for a panel from its physical
 * dimensions in millimetres. Used by panel hosts to size the chassis
 * frame deterministically.
 */
export function aspectRatioFromMm(widthMm: number, heightMm: number): string {
  return `${widthMm} / ${heightMm}`;
}
